import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../api/api";
import { API_ENDPOINTS, IN_APP_BROWSERS } from "../../config/constants";
import "../../styles/orderDetails.css";

// Download icon SVG
const DownloadIcon = ({ size = 20, className = "" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 20 20" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path 
      d="M10 2v10m0 0l-4-4m4 4l4-4" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
    <rect x="3" y="16" width="14" height="2" rx="1" fill="currentColor" />
  </svg>
);

// Back arrow icon
const BackIcon = ({ size = 20, className = "" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path 
      d="M15 18L9 12L15 6" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </svg>
);

// Helper for formatting date
function formatDate(dateStr) {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

// Helper for formatting order ID
function formatOrderId(orderId) {
  if (!orderId) return "-";
  const formattedOrderId = String(orderId).padStart(6, "0");
  return `#${formattedOrderId}`;
}

// Helper for status badge
function getStatusBadge(status) {
  if (!status) return null;
  const statusLower = status.toLowerCase().trim();
  let statusClass = 'status-badge ';
  let displayText = status;
  let icon = '';
  
  if (statusLower.includes('completed') || 
      statusLower.includes('delivered') || 
      statusLower.includes('success') ||
      statusLower.includes('paid')) {
    statusClass += 'status-completed';
    icon = '✅';
    displayText = statusLower.includes('paid') ? 'Paid' : 
                 statusLower.includes('delivered') ? 'Delivered' : 
                 statusLower.includes('success') ? 'Success' : 'Completed';
  } else if (statusLower.includes('processing') || 
           statusLower.includes('in_progress') ||
           statusLower.includes('in-progress') ||
           statusLower.includes('progress')) {
    statusClass += 'status-processing';
    icon = '⚙️';
    displayText = 'Processing';
  } else if (statusLower.includes('pending') || 
           statusLower.includes('waiting') ||
           statusLower.includes('initiated')) {
    statusClass += 'status-pending';
    icon = '⏳';
    displayText = statusLower.includes('waiting') ? 'Waiting' : 
                 statusLower.includes('initiated') ? 'Initiated' : 'Pending';
  } else if (statusLower.includes('failed') || 
           statusLower.includes('error') ||
           statusLower.includes('declined')) {
    statusClass += 'status-failed';
    icon = '❌';
    displayText = statusLower.includes('declined') ? 'Declined' : 
                 statusLower.includes('error') ? 'Error' : 'Failed';
  } else if (statusLower.includes('cancelled') || 
           statusLower.includes('canceled') ||
           statusLower.includes('refunded') ||
           statusLower.includes('refund')) {
    statusClass += 'status-cancelled';
    icon = '🚫';
    displayText = statusLower.includes('refund') ? 'Refunded' : 'Cancelled';
  } else {
    statusClass += 'status-default';
    icon = '📋';
    displayText = status;
  }
  
  return (
    <span className={statusClass}>
      {icon && <span className="status-icon">{icon}</span>}
      {displayText}
    </span>
  );
}

const OrderDetails = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState({
    invoice: false,
    remedy: false,
    report: false
  });
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Responsive detection
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const fetchOrderDetails = async () => {
      setLoading(true);
      const userId = JSON.parse(localStorage.getItem("user"))?.userid;
      try {
        const res = await api.get(`/orders/order/user/${userId}/${orderId}`);
        if (res.data.status === "success") {
          const orderData = res.data.data;
          
          // Add backward compatibility: create virtual order_item for orders without order_items
          if (!orderData.order_items || orderData.order_items.length === 0) {
            orderData.order_items = [{
              id: `virtual_${orderData.id}`,
              order_id: orderData.id,
              product_id: orderData.product_id,
              item_type: 'main',
              item_price: orderData.amount,
              delivery_status: orderData.delivery_status || null,
              reportjob_id: null,
              consultation_id: null,
              product_name: orderData.product_name,
              service_name: orderData.service_name,
              report_filename: orderData.report_filename || null,
              remedy_pdf_name: orderData.remedy_pdf_name || null,
              is_virtual: true // Flag to indicate this is a virtual item
            }];
            orderData.item_count = 1;
          }
          
          setOrder(orderData);
        } else {
          console.error("Failed to fetch order details");
        }
      } catch (err) {
        console.error("Error fetching order details:", err);
      }
      setLoading(false);
    };

    if (orderId) {
      fetchOrderDetails();
    }
  }, [orderId]);

  // In-app browser detection helper
   // Device detection helper
  const getMobileOS = () => {
    const ua = navigator.userAgent || navigator.vendor || window.opera;
    if (/android/i.test(ua)) return "Android";
    if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) return "iOS";
    return "Other";
  };

   // In-app browser detection, but only for non-iPhone devices
    const isInAppBrowser = () => {
      const ua = navigator.userAgent || navigator.vendor || window.opera;
      const os = getMobileOS();
      // Only return true if not iPhone/iOS
      if (os === "iOS") return false;
      return IN_APP_BROWSERS.test(ua);
    };
  
  // Handle copy download link for in-app browsers (item-level)
  const handleCopyDownloadLink = async (itemId, itemLabel, isVirtual = false) => {
    try {
      if (!order) return;
      
      // Route based on backward compatibility flag
      const endpoint = isVirtual 
        ? `/orders/remedy-pdf-token/${orderId}` // Old endpoint (orders table)
        : `/orders/remedy-pdf-token/${orderId}/${itemId}`; // New endpoint (order_items table)
      
      const { data } = await api.get(endpoint);
      if (data?.token) {
        const frontendBaseUrl = window.location.origin;
        // Generate URL based on endpoint type
        const downloadUrl = isVirtual
          ? `${frontendBaseUrl}/download/${orderId}?token=${data.token}`
          : `${frontendBaseUrl}/download/${orderId}/${itemId}?token=${data.token}`;
        await navigator.clipboard.writeText(downloadUrl);
        alert(`${itemLabel} download link copied! Open it in your browser to download the PDF.`);
      } else {
        alert('Could not generate download link. Please try again.');
      }
    } catch (err) {
      console.error('Copy link error:', err);
      alert('Could not copy download link. ' + (err?.message || ''));
    }
  };

  // Handle invoice download
  const handleDownloadInvoice = async () => {
    setDownloading({ ...downloading, invoice: true });
    try {
      const response = await api.download(`/invoices/download/${orderId}`);

      let filename = `invoice_${orderId}.pdf`;
      const disposition = response.headers["content-disposition"];
      if (disposition) {
        const match = disposition.match(/filename="?([^"]+)"?/);
        if (match && match[1]) {
          filename = match[1];
        }
      }

      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();

      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Invoice download error:", err);
      alert("Could not download invoice. " + (err?.message || ""));
    } finally {
      setDownloading({ ...downloading, invoice: false });
    }
  };

  // Handle remedy/report PDF download (item-level)
  const handleDownloadPDF = async (itemId, itemLabel, isVirtual = false) => {
    setDownloading({ ...downloading, [`item_${itemId}`]: true });
    
    try {
      // Route based on backward compatibility flag
      const endpoint = isVirtual
        ? `/orders/remedy-pdf-stream/${orderId}` // Old endpoint (orders table)
        : `/orders/remedy-pdf-stream/${orderId}/${itemId}`; // New endpoint (order_items table)
      
      const response = await api.download(endpoint);

      let filename = `${itemLabel.replace(/[^a-z0-9]/gi, '_')}_${orderId}.pdf`;
      const disposition = response.headers["content-disposition"] || 
                         response.headers["Content-Disposition"] ||
                         response.headers.get?.("content-disposition");
   
      if (disposition) {
        const match = disposition.match(/filename="?([^"]+)"?/);
        if (match && match[1]) {
          filename = match[1];
        }
      }

      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();

      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("PDF download error:", err);
      alert("Could not download PDF. " + (err?.message || ""));
    } finally {
      setDownloading({ ...downloading, [`item_${itemId}`]: false });
    }
  };

  if (loading) {
    return (
      <div className="order-details-page">
        <div className="order-details-loading">
          <div className="spinner"></div>
          <span>Loading order details...</span>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="order-details-page">
        <div className="order-details-error">
          <div className="error-icon">⚠️</div>
          <h3>Order not found</h3>
          <p>The order you're looking for doesn't exist or you don't have access to it.</p>
          <button className="btn-back" onClick={() => navigate(-1)}>
            <BackIcon size={16} />
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const isPaymentCompleted = order.payment_status === 'completed' || order.payment_status === 'success';
  const isDelivered = order.delivery_status === 'delivered' || order.status === 'delivered';

  return (
    <div className="order-details-page">
      <div className="order-details-container">
        {/* Header */}
        <div className="order-details-header">
          <button className="btn-back" onClick={() => navigate(-1)}>
            <BackIcon size={20} />
            Back
          </button>
          <h1 className="order-details-title">
            Order Details {formatOrderId(order.id)}
          </h1>
        </div>

        {/* Order Summary Card */}
        <div className="order-summary-card">
          <div className="order-summary-header">
            <h2>Order Summary</h2>
            {getStatusBadge(order.status)}
          </div>
          
          <div className="order-summary-grid">
            <div className="summary-item">
              <span className="summary-label">Order ID</span>
              <span className="summary-value">{formatOrderId(order.id)}</span>
            </div>
            
            <div className="summary-item">
              <span className="summary-label">Order Date</span>
              <span className="summary-value">{formatDate(order.created_at)}</span>
            </div>
            
            <div className="summary-item">
              <span className="summary-label">Amount</span>
              <span className="summary-value amount">₹{order.amount || "-"}</span>
            </div>
            
            <div className="summary-item">
              <span className="summary-label">Payment Status</span>
              <span className="summary-value">
                {getStatusBadge(order.payment_status || order.status)}
              </span>
            </div>
            
            <div className="summary-item">
              <span className="summary-label">Profile</span>
              <span className="summary-value">{order.profile_fullname || "-"}</span>
            </div>
          </div>
        </div>

        {/* Service Details Card */}
        <div className="details-card">
          <h3 className="card-title">
            <span className="card-icon">🛍️</span>
            Service Details
          </h3>
          <div className="details-grid">
            {/* Order Items */}
            {order.order_items && order.order_items.length > 0 && (
              <div className="detail-item full-width">
                {isMobile ? (
                  // Mobile: Card View
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {order.order_items.map((item, idx) => (
                      <div 
                        key={item.id}
                        style={{
                          padding: '16px',
                          backgroundColor: '#f9fafb',
                          borderRadius: '8px',
                          border: '1px solid #e5e7eb'
                        }}
                      >
                        {order.order_items.length > 1 && (
                          <div style={{ 
                            fontSize: '14px', 
                            fontWeight: '600', 
                            marginBottom: '12px',
                            color: '#374151',
                            borderBottom: '1px solid #e5e7eb',
                            paddingBottom: '8px'
                          }}>
                            Service {idx + 1}
                          </div>
                        )}
                        
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>Product:</span>
                            <span style={{ fontSize: '13px', color: '#111827', textAlign: 'right', flex: 1, marginLeft: '12px' }}>
                              {item.product_name || "-"}
                            </span>
                          </div>
                          
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>Service:</span>
                            <span style={{ fontSize: '13px', color: '#111827', textAlign: 'right', flex: 1, marginLeft: '12px' }}>
                              {item.service_name || "-"}
                            </span>
                          </div>
                          
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>Type:</span>
                            <span style={{ 
                              padding: '4px 10px', 
                              borderRadius: '4px', 
                              backgroundColor: item.item_type === 'main' ? '#dbeafe' : '#fef3c7',
                              color: item.item_type === 'main' ? '#1e40af' : '#92400e',
                              fontSize: '0.8em',
                              textTransform: 'capitalize',
                              fontWeight: '500'
                            }}>
                              {item.item_type || 'N/A'}
                            </span>
                          </div>
                          
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>Delivery:</span>
                            {getStatusBadge(item.delivery_status || 'pending')}
                          </div>
                          
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px', paddingTop: '10px', borderTop: '1px solid #e5e7eb' }}>
                            <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600' }}>Price:</span>
                            <span style={{ fontSize: '15px', color: '#111827', fontWeight: '600' }}>₹{item.item_price || 0}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  // Desktop: Table View
                  <div style={{ overflowX: 'auto', width: '100%' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f3f4f6', borderBottom: '2px solid #e5e7eb' }}>
                          <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Product</th>
                          <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Service</th>
                          <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600', color: '#374151' }}>Type</th>
                          <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600', color: '#374151' }}>Delivery Status</th>
                          <th style={{ padding: '12px', textAlign: 'right', fontWeight: '600', color: '#374151' }}>Price</th>
                        </tr>
                      </thead>
                      <tbody>
                        {order.order_items.map((item, idx) => (
                          <tr key={item.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                            <td style={{ padding: '12px' }}>
                              {item.product_name || "-"}
                            </td>
                            <td style={{ padding: '12px' }}>{item.service_name || "-"}</td>
                            <td style={{ padding: '12px', textAlign: 'center' }}>
                              <span style={{ 
                                padding: '4px 10px', 
                                borderRadius: '4px', 
                                backgroundColor: item.item_type === 'main' ? '#dbeafe' : '#fef3c7',
                                color: item.item_type === 'main' ? '#1e40af' : '#92400e',
                                fontSize: '0.85em',
                                textTransform: 'capitalize',
                                display: 'inline-block'
                              }}>
                                {item.item_type || 'N/A'}
                              </span>
                            </td>
                            <td style={{ padding: '12px', textAlign: 'center' }}>
                              {getStatusBadge(item.delivery_status || 'pending')}
                            </td>
                            <td style={{ padding: '12px', textAlign: 'right', fontWeight: '500' }}>₹{item.item_price || 0}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Downloads Card */}
        <div className="details-card downloads-card">
          <h3 className="card-title">
            <span className="card-icon">📥</span>
            Downloads
          </h3>
          
          <div className="downloads-grid">
            {/* Per-Item Downloads */}
            {order.order_items && order.order_items.length > 0 && order.order_items.map((item, idx) => {
              // Check conditions: item delivered AND payment completed AND file exists
              const isItemDelivered = item.delivery_status === 'delivered';
              const hasFile = item.report_filename || item.remedy_pdf_name;
              
              if (!isItemDelivered || !isPaymentCompleted || !hasFile) {
                return null;
              }

              // Determine which file to show (priority: report_filename first)
              const fileType = item.report_filename ? 'report' : 'remedy';
              const icon = fileType === 'report' ? '📊' : '🔮';
              const itemLabel = item.product_name || `Service ${idx + 1}`;

              return (
                <div key={item.id} className="download-item">
                  <div className="download-info">
                    <span className="download-icon">{icon}</span>
                    <div className="download-text">
                      <h4>{itemLabel}</h4>
                      <p>{item.service_name || 'Download your file'}</p>
                    </div>
                  </div>
                  {!isInAppBrowser() ? (
                    <button
                      className="btn-download"
                      onClick={() => handleDownloadPDF(item.id, itemLabel, item.is_virtual)}
                      disabled={downloading[`item_${item.id}`]}
                    >
                      {downloading[`item_${item.id}`] ? (
                        <>
                          <div className="btn-spinner"></div>
                          Downloading...
                        </>
                      ) : (
                        <>
                          <DownloadIcon size={18} />
                          Download
                        </>
                      )}
                    </button>
                  ) : (
                    <>
                      <button
                        className="btn-download"
                        onClick={() => handleCopyDownloadLink(item.id, itemLabel, item.is_virtual)}
                        disabled={downloading[`item_${item.id}`]}
                      >
                        <span style={{fontSize: '16px'}}>📋</span>
                        Copy Link
                      </button>
                      <div className="copy-link-instructions" style={{fontSize: '0.95em', color: '#555', marginTop: 8, lineHeight: 1.5, background: '#f8f8f8', borderRadius: 8, padding: '10px 12px'}}>
                        <ol style={{paddingLeft: 18, margin: 0}}>
                          <li>This app does not support file downloads.</li>
                          <li>Tap <b>Copy Link</b> above.</li>
                          <li>Open Chrome or any mobile browser.</li>
                          <li>Paste the link and tap Go/Enter.</li>
                          <li>Your download will start automatically.</li>
                          <li>The PDF will be saved in your Downloads folder.</li>
                        </ol>
                        <div style={{marginTop: 6, color: '#b36b00', fontSize: '0.97em'}}>
                          <b>Note:</b> Your file has also been sent to your WhatsApp.
                        </div>
                      </div>
                    </>
                  )}
                </div>
              );
            })}

            {/* Invoice Download (hidden in in-app browser) */}
            {!isInAppBrowser() && (
              <div className="download-item">
                <div className="download-info">
                  <span className="download-icon">📄</span>
                  <div className="download-text">
                    <h4>Invoice</h4>
                    <p>Download your order invoice</p>
                  </div>
                </div>
                {isPaymentCompleted ? (
                  <button
                    className="btn-download"
                    onClick={handleDownloadInvoice}
                    disabled={downloading.invoice}
                  >
                    {downloading.invoice ? (
                      <>
                        <div className="btn-spinner"></div>
                        Downloading...
                      </>
                    ) : (
                      <>
                        <DownloadIcon size={18} />
                        Download
                      </>
                    )}
                  </button>
                ) : (
                  <span className="download-unavailable">
                    Available after payment
                  </span>
                )}
              </div>
            )}

            {/* No downloads available message */}
            {!isPaymentCompleted && (
              <div className="no-downloads">
                <span className="no-downloads-icon">📭</span>
                <p>No downloads available yet. Complete payment to access your order files.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetails;
